/**
 * SEO utility — stroller slug map and shared helpers.
 *
 * STROLLER_SLUG_MAP maps internal product IDs (from /api/products) to the
 * descriptive, SEO-friendly URL slugs used in /strollers/:slug routes.
 * Keep this map in sync with _HARDCODED_STROLLER_SLUGS in backend/server.py.
 *
 * When a product has a stored `seo_slug` field, that takes priority over the
 * hardcoded map. Use getProductSlug(product) for the canonical slug.
 */

export const SITE_URL = "https://enchantedgearrentals.com";

export const DEFAULT_OG_IMAGE =
  "https://customer-assets.emergentagent.com/job_bubba-magic-rentals/artifacts/lup3nnvh_Untitled%20design.webp";

/** product_id → URL slug (legacy / backward-compatible hardcoded map) */
export const STROLLER_SLUG_MAP = {
  "bj-gt2-single": "baby-jogger-city-mini-gt2-single",
  "bob-single": "bob-single-stroller",
  "zoe-v2-single": "zoe-v2-single",
  "uppababy-vista": "uppababy-vista-v2",
  "uppababy-cruz": "uppababy-cruz-v2",
  "nuna-mixx": "nuna-mixx",
  "nuna-travel": "nuna-trvl",
  "bj-gt2-double": "baby-jogger-city-mini-gt2-double",
  "bj-gt-double": "baby-jogger-gt-double",
  "bj-city-mini-double": "baby-jogger-city-mini-double",
  "bob-double": "bob-double-stroller",
  "zoe-v1-twin": "zoe-v1-twin",
  "zoe-v2-twin": "zoe-v2-twin",
};

/** URL slug → product_id (reverse map, hardcoded slugs only) */
export const SLUG_TO_PRODUCT_ID = Object.fromEntries(
  Object.entries(STROLLER_SLUG_MAP).map(([id, slug]) => [slug, id])
);

/**
 * Return the canonical URL slug for a product object returned by the API.
 * Priority: stored seo_slug → hardcoded map → product id.
 *
 * @param {{ id: string, seo_slug?: string }} product
 * @returns {string}
 */
export const getProductSlug = (product) => {
  if (!product) return "";
  if (product.seo_slug && product.seo_slug.trim()) return product.seo_slug.trim();
  return STROLLER_SLUG_MAP[product.id] || product.id || "";
};

/**
 * Find a product from the products array by URL slug.
 * Supports both stored seo_slug and legacy hardcoded slugs.
 *
 * @param {Array} products - List from /api/products
 * @param {string} slug    - URL path segment
 * @returns {object|undefined}
 */
export const findProductBySlug = (products, slug) => {
  if (!products || !slug) return undefined;
  // 1. Try stored seo_slug
  const bySeoSlug = products.find(
    (p) => (p.seo_slug || "").trim().toLowerCase() === slug.toLowerCase()
  );
  if (bySeoSlug) return bySeoSlug;
  // 2. Try hardcoded slug map
  const idFromMap = SLUG_TO_PRODUCT_ID[slug];
  if (idFromMap) return products.find((p) => p.id === idFromMap);
  // 3. Direct ID match (fallback for products not in either map)
  return products.find((p) => p.id === slug);
};

/** Return the full canonical URL for a stroller product */
export const strollerUrl = (product) => {
  if (!product) return null;
  // Accept either a product object or a raw product ID string (legacy)
  if (typeof product === "string") {
    const slug = STROLLER_SLUG_MAP[product];
    return slug ? `${SITE_URL}/strollers/${slug}` : null;
  }
  const slug = getProductSlug(product);
  return slug ? `${SITE_URL}/strollers/${slug}` : null;
};

/** Normalize text into a URL-safe slug (client-side, mirrors the backend helper). */
export const slugify = (text) =>
  (text || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

/** Resolve a relative /api/... image path to an absolute URL */
export const resolveImage = (url, backendUrl) => {
  if (!url) return DEFAULT_OG_IMAGE;
  if (/^https?:\/\//i.test(url)) return url;
  return `${backendUrl}${url.startsWith("/") ? "" : "/"}${url}`;
};

/**
 * Return a human-readable product type label based on category and attributes.
 * Used for badges, spec labels, and on-page copy.
 *
 * @param {{ category?: string, stroller_attributes?: object, subcategory?: string, id?: string, name?: string }} product
 * @returns {string}
 */
export const getProductTypeLabel = (product) => {
  if (!product) return "Rental Item";
  const cat = (product.category || "").toLowerCase();
  if (cat === "ecvs") return "ECV";
  if (cat === "strollers" || cat === "bundles") {
    // Bundles: derive type label from subcategory first
    if (product.is_bundle) {
      const sub = (product.subcategory || "").toLowerCase().trim();
      if (sub === "trio" || sub === "triple" || sub === "triples") return "Triple Bundle";
      if (sub === "quad" || sub === "quads") return "Quad Bundle";
      if (sub === "double" || sub === "doubles" || sub === "twin") return "Double Bundle";
      if (sub === "extras") return "Extras Bundle";
      if (sub === "single" || sub === "singles") return "Single Bundle";
      return "Stroller Bundle";
    }
    const sa = product.stroller_attributes || {};
    const t = (sa.stroller_type || sa.seating_style || "").toLowerCase();
    if (t === "double" || t === "twin") return "Double Stroller";
    if (t === "triple") return "Triple Stroller";
    if (t === "quad") return "Quad Stroller";
    return "Single Stroller";
  }
  if (cat === "baby-gear") {
    const id = (product.id || "").toLowerCase();
    const name = (product.name || "").toLowerCase();
    if (id.includes("crib") || name.includes("crib")) return "Full-Size Crib";
    if (id.includes("chair") || name.includes("chair")) return "High Chair";
    if (id.includes("pack") || id.includes("play") || name.includes("pack")) return "Pack 'n Play";
    const sub = (product.subcategory || "").toLowerCase();
    if (sub === "sleep") return "Sleep Gear";
    if (sub === "mealtime") return "Feeding Gear";
    return "Baby Gear";
  }
  if (cat === "accessories") return "Accessory";
  return "Rental Item";
};

/**
 * Return a location-rich SEO page title for a product.
 * Respects admin-set seo_title — callers should check that first.
 *
 * @param {object} product
 * @returns {string}
 */
export const getProductPageTitle = (product) => {
  if (!product) return "Enchanted Gear Rentals";
  const cat = (product.category || "").toLowerCase();
  const suffix = "Near Disney World | Enchanted Gear Rentals";
  if (cat === "strollers" || cat === "bundles") return `${product.name} Stroller Rental ${suffix}`;
  if (cat === "ecvs") return `${product.name} ECV Rental ${suffix}`;
  if (cat === "baby-gear") return `${product.name} Rental ${suffix}`;
  return `${product.name} | Enchanted Gear Rentals`;
};

/**
 * Return a keyword-rich SEO meta description for a product.
 * Respects admin-set seo_description — callers should check that first.
 *
 * @param {object} product
 * @returns {string}
 */
export const getProductPageDescription = (product) => {
  if (!product) return "";
  const cat = (product.category || "").toLowerCase();
  const price = product.tier_1_3 ? ` From $${product.tier_1_3}/day.` : "";
  const tagline = product.tagline ? ` ${product.tagline}.` : "";
  if (cat === "strollers" || cat === "bundles") {
    return `Rent the ${product.name} near Walt Disney World and Orlando.${tagline} Free delivery to Disney resorts, Universal Orlando & area hotels.${price}`.trim();
  }
  if (cat === "ecvs") {
    return `Rent the ${product.name} ECV near Walt Disney World and Orlando.${tagline} Free delivery to Disney resorts, Universal Orlando and area hotels.${price}`.trim();
  }
  if (cat === "baby-gear") {
    return `Rent the ${product.name} near Walt Disney World and Orlando.${tagline} Free delivery to Disney resorts and area hotels.${price}`.trim();
  }
  return `Rent the ${product.name} near Orlando.${tagline} Free delivery to Disney resorts and area hotels.${price}`.trim();
};
